import { useEffect, useState } from 'react';
import { PageHeader, PageShell } from '@/components/layout';
import { NavigationPill } from '@/components/home';
import { SectionHeader } from '@/components/products';
import { RevealOnView } from '@/components/ui';
import { CONTENT_SLIDE, HEADER_FADE, REVEAL_TIMING } from '@/utils/animation';
import { ArticleDisclaimer } from './ArticleDisclaimer';
import { ArticleSection, ArticleSectionContent } from './ArticleSection';
import { useViewCatalogueLink } from './types';
import type { ArticlePageData } from './types';

// First section cascade — descriptor at 50% into the header fade, rest-of-section
// at 60% into the descriptor's slide. Same overlap rule the products page uses.
const REST_DELAY_MS =
  Math.round(HEADER_FADE.durationMs * 0.5) + Math.round(REVEAL_TIMING.durationMs * 0.6);

interface ArticlePageProps {
  data: ArticlePageData;
  /** Used as the SEO/document title and the canonical path. */
  seo: { title: string; description: string; path: string };
}

/**
 * Shared template for the article pages (Technology, Our story-style,
 * Patient care, Contact-style).
 *
 * Top-of-page strip uses the shared `PageHeader`. Below it the page-level
 * `SectionHeader` (title + intro), the divider, and the FIRST numbered
 * section all live in the same oat `<section>` — separated by `gap-30`
 * — so there's no double section-padding stacking up between the
 * divider and the first numbered row. Subsequent sections are rendered
 * as their own `<section>`s so the alternating tint still works.
 *
 * Section bg alternation:
 *   - Page-title + Section 01 (combined): oat (#FAFAFA)
 *   - Section 02:                          tinted (#E2DFDA at 20%)
 *   - …alternating thereafter. Disclaimer inherits the bg of the last
 *     numbered section.
 */
export function ArticlePage({ data, seo }: ArticlePageProps) {
  const viewCatalogueLink = useViewCatalogueLink();
  const [firstSection, ...restSections] = data.sections;
  const lastIndex = data.sections.length - 1;
  const lastTinted = lastIndex % 2 === 1;

  // 200ms beat after page-open before the first section's cascade kicks off —
  // matches the products-page Clear family trigger.
  const [mountedVisible, setMountedVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setMountedVisible(true), 200);
    return () => clearTimeout(t);
  }, []);

  return (
    <PageShell title={seo.title} description={seo.description} path={seo.path} hideHeader>
      <NavigationPill />
      <PageHeader title={seo.title} links={viewCatalogueLink} />

      {/* Page-level title + intro + first numbered section share one oat
          section. SectionHeader → hr → first section content, separated
          by `gap-30` (120px) — same rhythm Our Story uses between its
          intro and the About-us row. */}
      <section
        aria-labelledby="article-page-heading"
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
          <SectionHeader
            id="article-page-heading"
            title={data.pageTitle}
            intro={data.intro}
            visible={mountedVisible}
          />
          <RevealOnView
            {...CONTENT_SLIDE}
            inView={mountedVisible}
            delayMs={REST_DELAY_MS}
            className="flex flex-col gap-30"
          >
            <hr className="w-full border-0 border-t border-clearlab-divider-darker" />
            {firstSection ? <ArticleSectionContent section={firstSection} /> : null}
          </RevealOnView>
        </div>
      </section>

      {/* Remaining sections — index 1+ in the original array. Tint
          alternates starting tinted=true on section 02 so the visual
          rhythm matches the prior layout. */}
      {restSections.map((section, idx) => (
        <ArticleSection key={section.number} section={section} tinted={idx % 2 === 0} />
      ))}

      {data.disclaimer ? <ArticleDisclaimer text={data.disclaimer} tinted={lastTinted} /> : null}
    </PageShell>
  );
}
